import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Loader2,
  Play,
  TrendingUp,
} from 'lucide-react';
import {
  Bar,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
} from 'recharts';
import { apiClient } from '../../../lib/api';
import type { AnalysisSnapshot } from './types';
import { fmtMoney, fmtRatio } from './types';

/**
 * Analysis tab.
 *
 * Lists existing snapshots, lets the advisor trigger a fresh analysis
 * (POST /api/analysis/run), and renders the latest snapshot's headline KPIs
 * plus a year-by-year DSCR chart drawn from `summary.dynamic_dscr_projections`.
 */

type RunResult = AnalysisSnapshot;

export function AnalysisTab({ caseId }: { caseId: string }) {
  const qc = useQueryClient();

  const snapshotsQuery = useQuery<AnalysisSnapshot[]>({
    queryKey: ['analysis', 'snapshots', caseId],
    queryFn: () =>
      apiClient.get<AnalysisSnapshot[]>(`/analysis/snapshots?case_id=${caseId}`),
  });

  const run = useMutation<RunResult, Error, void>({
    mutationFn: () =>
      apiClient.post<RunResult>('/analysis/run', {
        case_id: caseId,
        snapshot_type: 'adhoc',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analysis', 'snapshots', caseId] });
    },
  });

  const snapshots = snapshotsQuery.data ?? [];
  const latest = useMemo(
    () =>
      snapshots
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )[0],
    [snapshots],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-xl font-semibold text-navy">ניתוח מלא</h2>
        <button
          type="button"
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="btn-primary inline-flex items-center gap-2"
        >
          {run.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          {run.isPending ? 'מריץ ניתוח…' : 'הרץ ניתוח חדש'}
        </button>
      </div>

      {run.isError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>הרצת הניתוח נכשלה: {run.error.message}</span>
        </div>
      )}

      {snapshotsQuery.isLoading ? (
        <div className="card text-center text-sm text-slate-500 py-8">טוען…</div>
      ) : snapshotsQuery.isError ? (
        <div className="card text-center text-sm text-red-600 py-8">
          שגיאה בטעינה: {(snapshotsQuery.error as Error).message}
        </div>
      ) : !latest ? (
        <div className="card text-center text-sm text-slate-500 py-12">
          <TrendingUp className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          עוד לא הורץ ניתוח. לחץ "הרץ ניתוח חדש" אחרי שהזנת חברות, דוחות
          ומסגרות אשראי.
        </div>
      ) : (
        <>
          <KPIBlock snapshot={latest} />
          {latest.summary.dynamic_dscr_projections &&
          latest.summary.dynamic_dscr_projections.length > 0 ? (
            <DSCRChart projections={latest.summary.dynamic_dscr_projections} />
          ) : (
            <div className="card text-sm text-slate-500 py-6 text-center">
              אין תחזית DSCR שנתית ב-snapshot זה (דורש מסגרות אשראי עם תקופה).
            </div>
          )}
          <SnapshotsList snapshots={snapshots} latestId={latest.snapshot_id} />
        </>
      )}
    </div>
  );
}

function KPIBlock({ snapshot }: { snapshot: AnalysisSnapshot }) {
  const s = snapshot.summary;
  return (
    <div className="grid md:grid-cols-4 gap-4">
      <KPI label="EBITDA" value={fmtMoney(s.ebitda)} suffix="א׳ ₪" />
      <KPI label="תזרים פנוי" value={fmtMoney(s.free_cash_flow)} suffix="א׳ ₪" />
      <KPI label="הון עצמי מוחשי" value={fmtMoney(s.tangible_equity)} suffix="א׳ ₪" />
      <KPI label="שנות החזר נורמטיביות" value={fmtRatio(s.normative_years)} />
      <KPI label="DSCR סטטי" value={fmtRatio(s.static_dscr)} highlight={dscrColor(s.static_dscr)} />
      <KPI
        label="DSCR מינימלי"
        value={fmtRatio(s.min_dscr)}
        sub={s.min_dscr_year != null ? `שנה ${s.min_dscr_year}` : undefined}
        highlight={dscrColor(s.min_dscr)}
      />
      <KPI
        label="שנים במצוקה"
        value={s.years_distressed != null ? String(s.years_distressed) : '—'}
        highlight={(s.years_distressed ?? 0) > 0 ? 'danger' : 'normal'}
      />
    </div>
  );
}

function dscrColor(v: number | null): 'normal' | 'danger' | 'warning' | 'success' {
  if (v == null) return 'normal';
  if (v < 1) return 'danger';
  if (v < 1.1) return 'warning';
  if (v >= 1.25) return 'success';
  return 'normal';
}

function KPI({
  label,
  value,
  sub,
  suffix,
  highlight = 'normal',
}: {
  label: string;
  value: string;
  sub?: string;
  suffix?: string;
  highlight?: 'normal' | 'success' | 'warning' | 'danger';
}) {
  const valueClass = {
    normal: 'text-navy',
    success: 'text-green-700',
    warning: 'text-amber-600',
    danger: 'text-red-600',
  }[highlight];
  return (
    <div className="card">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold num ${valueClass}`}>
        {value}
        {suffix && <span className="text-sm text-slate-400 mr-1">{suffix}</span>}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function DSCRChart({
  projections,
}: {
  projections: NonNullable<AnalysisSnapshot['summary']['dynamic_dscr_projections']>;
}) {
  const data = projections.map((p) => ({
    year: p.year,
    cashFlow: p.cash_flow,
    debtService: p.total_debt_service,
    dscr: p.dscr ?? 0,
  }));

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-navy mb-4">DSCR דינמי לאורך חיי ההלוואות</h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <XAxis dataKey="year" tick={{ fontSize: 12 }} />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12 }}
              tickFormatter={(v) => new Intl.NumberFormat('he-IL').format(v as number)}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 'auto']}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(v: unknown, name) => {
                if (name === 'dscr') return [Number(v).toFixed(2), 'DSCR'];
                return [
                  new Intl.NumberFormat('he-IL').format(Number(v)),
                  name === 'cashFlow' ? 'תזרים' : 'שירות חוב',
                ];
              }}
              labelFormatter={(label) => `שנה ${label}`}
            />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="cashFlow"
              name="תזרים פנוי"
              fill="#2C5F8D"
            />
            <Bar
              yAxisId="left"
              dataKey="debtService"
              name="שירות חוב"
              fill="#C89B3C"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="dscr"
              name="DSCR"
              stroke="#10B981"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SnapshotsList({
  snapshots,
  latestId,
}: {
  snapshots: AnalysisSnapshot[];
  latestId: string;
}) {
  return (
    <div className="card !p-0 overflow-hidden">
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <h3 className="font-semibold text-navy">היסטוריית ניתוחים</h3>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white text-slate-600">
          <tr>
            <th className="px-4 py-2 text-right font-medium">תאריך</th>
            <th className="px-4 py-2 text-left font-medium">EBITDA</th>
            <th className="px-4 py-2 text-left font-medium">FCF</th>
            <th className="px-4 py-2 text-left font-medium">DSCR מינ׳</th>
            <th className="px-4 py-2 text-left font-medium">שנים במצוקה</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {snapshots
            .slice()
            .sort(
              (a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            )
            .map((s) => (
              <tr
                key={s.snapshot_id}
                className={s.snapshot_id === latestId ? 'bg-business-primary/5' : ''}
              >
                <td className="px-4 py-2">
                  {new Date(s.created_at).toLocaleString('he-IL')}
                </td>
                <td className="px-4 py-2 text-left num">{fmtMoney(s.summary.ebitda)}</td>
                <td className="px-4 py-2 text-left num">
                  {fmtMoney(s.summary.free_cash_flow)}
                </td>
                <td className="px-4 py-2 text-left num">{fmtRatio(s.summary.min_dscr)}</td>
                <td className="px-4 py-2 text-left num">
                  {s.summary.years_distressed ?? '—'}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
